import { MouseEventHandler } from 'react';
import { BiSolidShoppingBags } from 'react-icons/bi';
import { BsShop } from 'react-icons/bs';
import { CiSettings, CiViewList } from 'react-icons/ci';
import { FaRobot, FaUsers, FaVideo } from 'react-icons/fa';
import { FaRegFileLines } from 'react-icons/fa6';
import { GrTextAlignLeft } from 'react-icons/gr';
import { IoSettings } from 'react-icons/io5';
// import { LuFileLineChart } from 'react-icons/lu';
import { MdOutlineSupportAgent, MdSpaceDashboard } from 'react-icons/md';
import { RiDiscountPercentFill, RiDiscountPercentLine } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';

import { LiveHagglesIcon } from '../ui/svg/NavIcons';
import { checkUserRoleRubyOrSapphireOrDiamond } from '../utils/utils';

import ProductIcon from '@/assets/menu/ProductIcon';
import { useUser } from '@/hooks/useUser';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';

export interface INavigationItem {
	label: string;
	value?: string | null;
	path: string | null;
	onClick: MouseEventHandler<HTMLButtonElement>;
	icon: JSX.Element;
	requiresAdmin: boolean;
}

const DashboardNavigationList = () => {
	const { user } = useUser();
	const userIsAdmin = user?.roles.includes('admin');
	const userIsDiamondOrRuby =
		user?.roles.includes(UserRole.MerchantDiamond) ||
		user?.roles.includes(UserRole.MerchantRuby);
	const userIsDiamond = user?.roles.includes(UserRole.MerchantDiamond);
	const userIsSapphireOrRubyOrDiamond = checkUserRoleRubyOrSapphireOrDiamond(
		user?.roles ?? [],
	);
	const location = useLocation();
	const navigate = useNavigate();
	const isCurrentRoute = (path: string | null) => {
		if (path === '' && location.pathname === '/') return true;

		if (!path) return false;

		// Require an exact match only for the main dashboard route
		if (path === AppRoutes.UserDashboard) {
			return location.pathname === path;
		}

		// For all other routes
		return location.pathname.startsWith(path);
	};

	const adminMenu: INavigationItem[] = [
		{
			label: 'Haggle Hub',
			onClick: () => navigate('/'),
			icon: <LiveHagglesIcon />,
			path: ``,
			requiresAdmin: false,
		},
		{
			label: 'Merchants',
			onClick: () => navigate('merchants'),
			icon: <BiSolidShoppingBags size={20} />,
			path: `${AppRoutes.User}/merchants`,
			requiresAdmin: true,
		},
		{
			label: 'Dashboard',
			onClick: () => navigate('dashboard'),
			icon: <MdSpaceDashboard size={20} />,
			path: AppRoutes.UserDashboard,
			requiresAdmin: false,
		},
		{
			label: 'Haggle Events',
			// value optional key is added cause multiple tests were failing, admin-menu-Haggles is expected to be visible
			value: 'Haggles',
			onClick: () =>
				userIsAdmin ? navigate('haggles') : navigate('dashboard/haggle-events'),
			icon: <RiDiscountPercentLine size={20} />,
			path: userIsAdmin
				? `${AppRoutes.User}/haggles`
				: AppRoutes.UserHaggleEvents,
			requiresAdmin: false,
		},
		{
			label: 'Users',
			onClick: () =>
				userIsAdmin ? navigate('users') : navigate('dashboard/users'),
			icon: <FaUsers size={20} />,
			path: userIsAdmin ? AppRoutes.AdminUsers : AppRoutes.UserUsers,
			requiresAdmin: false,
		},
		...(userIsDiamond
			? [
					{
						label: 'Haggle Invited',
						onClick: () => navigate('dashboard/haggle-invited'),
						icon: <FaUsers size={20} />,
						path: AppRoutes.HaggleInvitedUsers,
						requiresAdmin: false,
					},
			  ]
			: []),

		{
			label: 'Invited Users',
			onClick: () => navigate('invited'),
			icon: <FaUsers size={20} />,
			path: `${AppRoutes.User}/invited`,
			requiresAdmin: true,
		},
		{
			label: 'Configure App',
			onClick: () => navigate('configure-app'),
			icon: <IoSettings size={20} />,
			path: `${AppRoutes.AdminConfigure}`,
			requiresAdmin: true,
		},
		{
			label: 'Prompts',
			onClick: () => navigate('prompts'),
			icon: <GrTextAlignLeft size={20} />,
			path: `${AppRoutes.User}/prompts`,
			requiresAdmin: true,
		},
		{
			label: 'Shopify Discount',
			onClick: () => navigate('shopify-subscription-discounts'),
			icon: <RiDiscountPercentFill size={20} />,
			path: `${AppRoutes.User}/shopify-subscription-discounts`,
			requiresAdmin: true,
		},
		{
			label: 'Products',
			onClick: () =>
				userIsAdmin ? navigate('products') : navigate('dashboard/products'),
			icon: <ProductIcon />,
			path: userIsAdmin ? AppRoutes.AdminProducts : AppRoutes.UserProducts,
			requiresAdmin: false,
		},

		{
			label: 'Reports',
			onClick: () =>
				userIsAdmin ? navigate('reports') : navigate('dashboard/reports'),
			icon: <FaRegFileLines size={20} />,
			path: userIsAdmin ? AppRoutes.AdminReports : AppRoutes.UserReports,
			requiresAdmin: false,
		},
		// {
		// 	label: 'Billing',
		// 	onClick: () => navigate('billing'),
		// 	icon: <LuFileLineChart size={20} />,
		// 	path: `${AppRoutes.User}/billing`,
		// 	requiresAdmin: false,
		// },
		{
			label: 'Orders',
			onClick: () => navigate('dashboard/orders'),
			icon: <CiViewList size={20} />,
			path: `${AppRoutes.Orders}`,
			requiresAdmin: false,
		},

		...(userIsSapphireOrRubyOrDiamond
			? [
					{
						label: 'Customization',
						onClick: () => navigate('customize-chatbot'),
						icon: <FaRobot size={20} />,
						path: `${AppRoutes.User}/customize-chatbot`,
						requiresAdmin: false,
					},
			  ]
			: []),

		...(userIsDiamondOrRuby
			? [
					{
						label: 'B2B Marketplace',
						onClick: () => navigate('b2b-vendors'),
						icon: <BsShop size={20} />,
						path: `${AppRoutes.User}/b2b-vendors`,
						requiresAdmin: false,
					},
			  ]
			: []),
		{
			label: 'Settings',
			onClick: () => navigate('settings'),
			icon: <CiSettings size={20} />,
			path: `${AppRoutes.User}/settings`,
			requiresAdmin: false,
		},
		{
			label: 'How-to Videos',
			onClick: () => navigate('dashboard/how-to-videos'),
			icon: <FaVideo size={20} />,
			path: AppRoutes.HowToVideos,
			requiresAdmin: false,
		},
		{
			label: 'Support',
			onClick: () => navigate('support'),
			icon: <MdOutlineSupportAgent size={20} />,
			path: `${AppRoutes.User}/support`,
			requiresAdmin: false,
		},
	];

	const filteredMenu = adminMenu.filter((item) => {
		if (userIsAdmin && item.label === 'Dashboard') {
			return false;
		}
		return !item.requiresAdmin || userIsAdmin;
	});

	return (
		<div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-4 py-3.5">
			<div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-4">
				{filteredMenu.map(({ label: name, icon, path, value }) => (
					<button
						data-test={`admin-menu-${value ? value : name}`}
						className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-100
          ${
						isCurrentRoute(path) &&
						'bg-grey-10 border-l-4 border-purpleBrand-500 font-semibold'
					}`}
						onClick={() => {
							if (path === '') navigate('/');
							else {
								const targetPath = path?.startsWith('/user')
									? path
									: `/user/${path?.replace(/^\/+/, '')}`;
								navigate(targetPath);
							}
						}}
						key={name}
					>
						{icon}
						<span
							className={`text-md ${
								isCurrentRoute(path) && 'text-purpleBrand-500'
							}`}
						>
							{name}
						</span>
					</button>
				))}
			</div>
		</div>
	);
};

export default DashboardNavigationList;
